import get from 'lodash/get'
import set from 'lodash/set'
import map from 'lodash/map'
import find from 'lodash/find'
import uniq from 'lodash/uniq'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import isNumber from 'lodash/isNumber'
import { fetchMultipleNodes } from '../node/index'
import { getUserPublic } from '../user/index'
import {
  REQUEST_ACCEPT_APPLICATION_JSON, TYPE_BROOKLYN, post as apiPost, get as apiGet,
} from '../../api-client/index'
import { TYPES as SUBSCRIPTION_TYPES } from './api-subscriptions'

const ERROR = { success: false }

export const TYPES = {
  SUBSCRIPTION_RECOMMENDATION: 'SUBSCRIPTION_RECOMMENDATION',
  SUBSCRIPTION_NEW_EPISODE: 'SUBSCRIPTION_NEW_EPISODE',
  SUBSCRIPTION_NEW_SEASON: 'SUBSCRIPTION_NEW_SEASON',
  SUBSCRIPTION_FREE_FORM: 'SUBSCRIPTION_FREE_FORM',
  GENERAL_VIDEO_PROMOTION: 'GENERAL_VIDEO_PROMOTION',
  GENERAL_RECOMMENDATION: 'GENERAL_RECOMMENDATION',
  GENERAL_NEW_FEATURE: 'GENERAL_NEW_FEATURE',
  GENERAL_FREE_FORM: 'GENERAL_FREE_FORM',
  MEMBER_RECOMMENDATION: 'MEMBER_RECOMMENDATION',
  MEMBER_PLAYLIST_ADD: 'MEMBER_PLAYLIST_ADD',
  MEMBER_FREE_FORM: 'MEMBER_FREE_FORM',
  MEMBER_FOLLOWING: 'MEMBER_FOLLOWING',
  ACCOUNT_FOLLOWED: 'ACCOUNT_FOLLOWED',
  CIRCLE_NEW_MESSAGE: 'CIRCLE_NEW_MESSAGE',
  CIRCLE_OPENED: 'CIRCLE_OPENED',
  CIRCLE_INVITE: 'CIRCLE_INVITE',
  COMMENT_REJECTED: 'COMMENT_REJECTED',
  PROFILE_FOLLOWED: 'PROFILE_FOLLOWED',
}

export const CONTENT_TYPES = {
  SUBSCRIPTION: 'subscription',
  MEMBER: 'member',
  SERIES: 'series',
  VIDEO: 'video',
}

function getNodeContentId (notification) {
  const type = get(notification, 'type')
  if (type === TYPES.SUBSCRIPTION_FREE_FORM) {
    const subscriptionType = get(notification, ['subscriptions', 0, 'type'])
    if (subscriptionType === SUBSCRIPTION_TYPES.SERIES) {
      return get(notification, ['subscriptions', 0, 'contentId'])
    }
  }
  return get(notification, 'contentId')
}

function getMemberContentId (notification) {
  const subType = get(notification, ['subscriptions', 0, 'type'])
  if (subType === SUBSCRIPTION_TYPES.MEMBER) {
    return get(notification, ['subscriptions', 0, 'contentId'], null)
  }
  if (subType === SUBSCRIPTION_TYPES.ACCOUNT) {
    return get(notification, 'contentId', null)
  }
  return null
}

async function getNodeContent (notifications, language, auth) {
  const ids = uniq(map(notifications, getNodeContentId))
  const validIds = filter(ids, isNumber)

  const rawContents = await fetchMultipleNodes({ ids: validIds, language, auth })
  const contents = rawContents.map((item) => item.data)
  return map(notifications, (n) => {
    const contentId = getNodeContentId(n)
    const content = find(contents, (c) => (
      get(c, 'nid') === contentId
    ))
    if (content) {
      set(n, 'node', content)
    }
    return n
  })
}

async function getMemberContent (notifications) {
  const ids = uniq(map(notifications, getMemberContentId))
  const validIds = filter(ids, (id) => !!id)
  const requests = map(validIds, (id) => (
    getUserPublic(id)
  ))
  const contents = await Promise.all(requests)
  return map(notifications, (n) => {
    const contentId = getMemberContentId(n)
    const content = find(contents, (c) => (
      get(c, 'id') === contentId
    ))
    if (content) {
      set(n, 'member', content)
    }
    return n
  })
}

/**
 * Which notifications are about a member identified by `actorUuid` rather than by `contentId`.
 *
 * Today that is only the Community profile follow (CO-735/CO-737). The legacy ACCOUNT_FOLLOWED and
 * MEMBER_FOLLOWING types are the OTHER shape — a member reached through `contentId` / the
 * subscription — and are handled by `getMemberContent`.
 */
function getActorContentUuid (notification) {
  if (get(notification, 'type') !== TYPES.PROFILE_FOLLOWED) {
    return null
  }
  return get(notification, 'actorUuid', null)
}

/**
 * Attaches the actor's picture to the notifications that carry an `actorUuid`.
 *
 * ⚠️ Deliberately NOT routed through `getMemberContent`, for three separate reasons:
 *
 * 1. That one keys on `contentId`, and a profile follow has none at all — the actor is the whole
 *    payload.
 * 2. Its `transform()` builds `/portal/<url>`, the legacy Gaia Portal route. A Community profile
 *    lives at `/profile/<uuid>`.
 * 3. Its `transform()` maps `title` to the member's USERNAME, where the server already sends the
 *    display name as `name` — which is what every other Community surface renders.
 *
 * So only the picture is taken from the lookup; the name and the link come off the notification
 * itself. `getUserPublic` is genuinely uuid-keyed (`/v1/user/lookup/:uuid`), so passing an
 * `actorUuid` to it is correct by construction.
 *
 * A member whose lookup fails or who has no picture simply keeps no image — `renderFollow` already
 * draws nothing rather than a broken one.
 */
export async function getActorContent (notifications) {
  const uuids = uniq(compact(map(notifications, getActorContentUuid)))
  // ⚠️ The early return is load-bearing, not a micro-optimisation. `getRecentNotifications` polls
  // every 60s for every signed-in member, and its own comment calls this hydration "the bulk of the
  // request's database cost". A page with no follow notifications must cost nothing extra.
  if (uuids.length < 1) {
    return notifications
  }
  const contents = await Promise.all(map(uuids, (uuid) => getUserPublic(uuid)))
  return map(notifications, (n) => {
    const uuid = getActorContentUuid(n)
    if (!uuid) {
      return n
    }
    // ⚠️ `id`, not `uuid`: `transform()` maps the member's uuid onto `id` (user/index.js:500),
    // which is also how getMemberContent matches. Keying on `uuid` here silently never hits.
    const content = find(contents, (c) => get(c, 'id') === uuid)
    set(n, 'member', {
      image: get(content, 'image', ''),
      title: get(n, 'name', ''),
      url: `/profile/${uuid}`,
    })
    return n
  })
}

export async function getNotifications ({ auth, language }) {
  try {
    const query = { p: 1, pp: 50, language }
    const params = { auth }
    const response = await apiGet('/v3/notifications/user', query, params, TYPE_BROOKLYN)
    const body = get(response, 'body', {})

    const notifications = get(body, 'notifications', {})
    const contentWithNodes = await getNodeContent(notifications, language, auth)
    const contentWithMembers = await getMemberContent(contentWithNodes)
    const contentWithActors = await getActorContent(contentWithMembers)

    return {
      rows: contentWithActors,
      total: get(body, 'total'),
      pp: get(body, 'pp'),
      p: get(body, 'p'),
    }
  } catch (e) {
    return ERROR
  }
}

const sameIdList = (a, b) => (
  a.length === b.length && a.every((id, i) => id === b[i])
)

export async function getRecentNotifications ({ auth, language, previousIds = [] }) {
  try {
    const query = { p: 1, pp: 10, language }
    const params = { auth }
    const response = await apiGet('/v3/notifications/user', query, params, TYPE_BROOKLYN)
    const notifications = get(response, ['body', 'notifications'], {})

    // Steady-state short-circuit: when the fetched notification ids match what
    // the store already holds (the common case — no new notification since the
    // last poll), skip the node/member hydration. getNodeContent and
    // getMemberContent are each a Brooklyn round-trip (fetchMultipleNodes +
    // getUserPublic per member), so at a 60s poll x every circle member this
    // hydration is the bulk of the request's database cost.
    const ids = map(notifications, (n) => get(n, 'id'))
    if (previousIds.length && sameIdList(ids, previousIds)) {
      return { unchanged: true }
    }

    const contentWithNodes = await getNodeContent(notifications, language, auth)
    const contentWithMembers = await getMemberContent(contentWithNodes)
    const contentWithActors = await getActorContent(contentWithMembers)

    return { data: contentWithActors }
  } catch (e) {
    return ERROR
  }
}

export async function setNotificationsReceived ({ ids, auth }) {
  try {
    const data = { notifications: ids }
    const params = { reqType: REQUEST_ACCEPT_APPLICATION_JSON, auth }
    await apiPost('/notifications/user/received', data, params, TYPE_BROOKLYN)
    return { success: true }
  } catch (e) {
    return ERROR
  }
}
